import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Grid,
  MenuItem,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { format } from 'date-fns';

interface PosTransaction {
  id: string;
  provider: string;
  terminalId: string;
  referenceNo: string;
  amount: number;
  status: string;
  createdAt: string;
}

const PosPaymentReports: React.FC = () => {
  const [transactions, setTransactions] = useState<PosTransaction[]>([]);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    terminalId: '',
  });

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.terminalId) params.append('terminalId', filters.terminalId);

      const response = await axios.get(`/system-settings/payments/reports/pos?${params.toString()}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS': return 'success';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">POS Transaction Reports</Typography>
        <Box>
          <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ mr: 1 }}>
            Export PDF
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            Export Excel
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate}
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SUCCESS">Success</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Terminal ID"
              value={filters.terminalId}
              onChange={(e) => setFilters({ ...filters, terminalId: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Terminal ID</TableCell>
              <TableCell>Reference No</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx) => (
              <TableRow key={tx.id}>
                <TableCell>{format(new Date(tx.createdAt), 'dd MMM yyyy HH:mm')}</TableCell>
                <TableCell>{tx.provider}</TableCell>
                <TableCell>{tx.terminalId}</TableCell>
                <TableCell>{tx.referenceNo}</TableCell>
                <TableCell>₹{tx.amount.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip 
                    label={tx.status} 
                    color={getStatusColor(tx.status) as any} 
                    size="small" 
                  />
                </TableCell>
              </TableRow>
            ))}
            {transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No transactions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PosPaymentReports;
